import { defineStore } from "pinia";
import payments, {
    BulkCompetitorPaymentUpdate,
    CompetitorPaymentsSearchRequest,
    CompetitorPaymentWithMetadata,
    PaymentConfirmationFilter,
    SortDirection,
    SortField,
} from "../Api/payments";
import { DEFAULT_SORT_DIRECTION, DEFAULT_SORT_FIELD } from "./adminRegisteredCompetitorsApp";
import startingListsApi, { StartingListsSharingState, StartingListsSharingType } from "../Api/startingListsApi";
import { handleError } from "../Common/errorHandler";
import history from "../Common/history";

export const PAGE_SIZE = 25;

export type BulkSelectionType = "checkbox" | "searchCriteria";

export const useRegisteredCompetitorsStore = defineStore("registeredCompetitors/main", {
    state: () => ({
        eventId: 0,
        eventTimezone: "",
        isFullOrganizer: false,
        // The logged-in organizer's own user id - not to be confused with `userId` below,
        // which is a competitor-filter query param.
        currentUserId: 0,

        registeredCompetitors: [] as CompetitorPaymentWithMetadata[],
        offset: 0,
        hasNextPage: false,
        hasPrevPage: false,
        query: "",
        deleted: false,
        parameterId: 0,
        userId: 0,
        selectedCategoryName: null as unknown as string | null,

        onlyMissingPayments: false,
        onlyCompletePayments: false,
        showDisqualified: false,

        paymentConfirmationFilter: "all" as PaymentConfirmationFilter,

        sortField: DEFAULT_SORT_FIELD as SortField,
        sortDirection: DEFAULT_SORT_DIRECTION as SortDirection,

        initDone: false,

        startingListsSharing: null as StartingListsSharingState | null,

        //Bulk mode.
        bulkModeEnabled: false,
        selectionType: "checkbox" as BulkSelectionType,
        selectedCompetitors: {} as Record<number, boolean>,
        pageToggled: false,
        matchingRows: 0,
        bulkModeTimestamp: null as string | null,
    }),

    actions: {
        loadStartingListsSharing(): Promise<void> {
            return startingListsApi.getSharing(this.eventId).then(sharing => {
                this.startingListsSharing = sharing;
            });
        },
        changeStartingListsSharingType(sharingType: StartingListsSharingType): Promise<void> {
            return startingListsApi.changeSharingType(this.eventId, { sharingType }).then(sharing => {
                this.startingListsSharing = sharing;
            });
        },

        // Bulk actions
        togglePage(selected: boolean) {
            this.pageToggled = selected;

            this.registeredCompetitors.forEach(registeredCompetitor => {
                this.selectedCompetitors[registeredCompetitor.id] = selected;
            });
            this.selectionType = "checkbox";
        },
        toggleSelectedCompetitor(eventRegistrationId: number, selected: boolean) {
            this.selectedCompetitors[eventRegistrationId] = selected;
            this.selectionType = "checkbox";
        },

        increaseOffset() {
            this.offset += PAGE_SIZE;
        },
        decreaseOffset() {
            this.offset = Math.max(0, this.offset - PAGE_SIZE);
        },

        async enableBulkMode(): Promise<void> {
            this.selectedCompetitors = {};
            this.selectionType = "checkbox";
            this.pageToggled = false;

            return payments
                .countCompetitorPayments(this.eventId, {
                    query: this.query,
                    deleted: this.deleted,
                    parameterId: this.parameterId,
                    userId: this.userId,
                    onlyMissingPayments: this.onlyMissingPayments,
                    onlyCompletePayments: this.onlyCompletePayments,
                    disqualified: this.showDisqualified,
                    paymentConfirmationFilter: this.paymentConfirmationFilter,
                })
                .then(response => {
                    this.matchingRows = response.count;
                    this.bulkModeEnabled = true;
                    this.bulkModeTimestamp = response.timestamp;
                })
                .catch(handleError);
        },
        cancelBulkMode() {
            this.selectionType = "checkbox";
            this.bulkModeEnabled = false;
            this.selectedCompetitors = {};
            this.pageToggled = false;
        },
        // Resets the search query and the deleted/disqualified/payment filters; category, user and sorting stay.
        resetFilters() {
            const historyInstance = history.getInstance(window);

            this.query = "";
            this.deleted = false;
            this.showDisqualified = false;
            this.onlyMissingPayments = false;
            this.onlyCompletePayments = false;
            this.paymentConfirmationFilter = "all";

            historyInstance.setQueryParam("q", null);
            historyInstance.setQueryParam("deleted", null);
            historyInstance.setQueryParam("disqualified", null);
            historyInstance.setQueryParam("onlyMissingPayments", null);
            historyInstance.setQueryParam("onlyCompletePayments", null);
            historyInstance.setQueryParam("paymentConfirmationFilter", null);
        },
        // Shows the full roster of one category; sorting is a view preference and stays.
        showOnlyCategory(parameterId: number) {
            const historyInstance = history.getInstance(window);

            this.cancelBulkMode();
            this.resetFilters();
            this.parameterId = parameterId;
            this.userId = 0;
            this.offset = 0;

            historyInstance.setQueryParam("parameterId", parameterId);
            historyInstance.setQueryParam("userId", null);
            historyInstance.setQueryParam("offset", 0);

            return this.getRegisteredCompetitorsPage().catch(handleError);
        },
        selectBySearchCriteria() {
            this.togglePage(true);
            this.selectionType = "searchCriteria";
        },

        async getRegisteredCompetitorsPage() {
            return payments
                .getCompetitorPaymentsPage(this.eventId, {
                    offset: this.offset,
                    query: this.query,
                    limit: PAGE_SIZE,
                    sortField: this.sortField,
                    sortDirection: this.sortDirection,
                    deleted: this.deleted,
                    parameterId: this.parameterId,
                    userId: this.userId,
                    onlyMissingPayments: this.onlyMissingPayments,
                    onlyCompletePayments: this.onlyCompletePayments,
                    disqualified: this.showDisqualified,
                    paymentConfirmationFilter: this.paymentConfirmationFilter,
                })
                .then(registeredCompetitorsPage => {
                    this.selectedCategoryName = registeredCompetitorsPage.selectedCategoryName;
                    this.registeredCompetitors = registeredCompetitorsPage.result;
                    this.hasNextPage = registeredCompetitorsPage.hasNextPage;
                    this.hasPrevPage = registeredCompetitorsPage.hasPrevPage;
                    this.initDone = true;

                    if (this.bulkModeEnabled) {
                        //Re-enabling will reset selections and refresh the count.
                        return this.enableBulkMode();
                    }
                    return Promise.resolve();
                });
        },
        async bulkUpdateCompetitorPayments(complete: boolean): Promise<void> {
            if (!this.bulkModeTimestamp) {
                return Promise.reject(new Error("Bulk mode is not aware of the current timestamp."));
            }

            const searchCriteria =
                this.selectionType === "checkbox"
                    ? ({
                          eventRegistrationIds: Object.keys(this.selectedCompetitors)
                              .filter(key => this.selectedCompetitors[Number(key)])
                              .map(key => Number(key)),
                      } satisfies CompetitorPaymentsSearchRequest)
                    : ({
                          query: this.query || null,
                          deleted: this.deleted || false,
                          parameterId: this.parameterId || null,
                          userId: this.userId || null,
                          onlyMissingPayments: this.onlyMissingPayments || false,
                          onlyCompletePayments: this.onlyCompletePayments || false,
                          disqualified: this.showDisqualified || false,
                          paymentConfirmationFilter: this.paymentConfirmationFilter || "all",
                      } satisfies CompetitorPaymentsSearchRequest);

            return payments.bulkUpdateCompetitorPayments(this.eventId, {
                complete,
                searchCriteria,
                timestamp: this.bulkModeTimestamp,
            } satisfies BulkCompetitorPaymentUpdate);
        },
    },
});
